import { useMutation, useQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { track } from "@amplitude/analytics-browser";
import type { LikedStoresQuery } from "../../../__generated__/graphql";
import type { LikedStoreItem } from "./use-infinite-scroll-liked-stores";
import { useLocation } from "../../../core/location-provider";
import { CANCEL_LIKE, LIKE_STORE } from "../../../api/store-mutations";
import { GET_LIKED_STORES } from "../api/liked-stores-queries";
import { getRestoredLikedStores } from "../utils/restore-liked-stores";
import { useInfiniteScrollLikedStores } from "./use-infinite-scroll-liked-stores";

const STORAGE_KEY = "like";
const LIMIT = 20;

export function useLikedStores() {
  const navigate = useNavigate();
  const { hasLastLo, getLocationFromStorage } = useLocation();

  const currentLocationKey = useMemo(
    () =>
      hasLastLo
        ? `${getLocationFromStorage().lat}-${getLocationFromStorage().lng}`
        : "null-null",
    [hasLastLo, getLocationFromStorage]
  );

  const tempRestoredData = useMemo(
    () => getRestoredLikedStores(STORAGE_KEY, currentLocationKey),
    [currentLocationKey]
  );

  const { loading, fetchMore, data } = useQuery(GET_LIKED_STORES, {
    variables: {
      lat: hasLastLo ? getLocationFromStorage().lat : null,
      lng: hasLastLo ? getLocationFromStorage().lng : null,
      offset: 0,
      limit: LIMIT,
    },
    fetchPolicy: "network-only",
    skip: !!tempRestoredData,
  });

  const {
    rootRef,
    loadMoreRef,
    allStores,
    setAllStores,
    hasMore,
    isLoadingMore,
    initLoading,
  } = useInfiniteScrollLikedStores({
    limit: LIMIT,
    hasLastLo,
    getLocationFromStorage,
    queryLoading: loading,
    fetchMore,
    storageKey: STORAGE_KEY,
  });

  // 첫 페이지 결과를 allStores에 동기화 (복원된 데이터가 없을 때만)
  useEffect(() => {
    if (!!tempRestoredData || loading || !data?.likedStores) return;
    if (allStores.length === 0) {
      setAllStores(data.likedStores as NonNullable<LikedStoresQuery["likedStores"]>);
    }
  }, [tempRestoredData, loading, data?.likedStores, allStores.length, setAllStores]);

  const [likeStore] = useMutation(LIKE_STORE, {
    update: (cache, { data: mutationData }) => {
      const likeStoreResult = mutationData?.likeStore;
      if (!likeStoreResult) return;
      const newStoreRef = cache.writeFragment({
        data: likeStoreResult,
        fragment: gql(`
          fragment NewStore on Store {
            id
            isLiked
          }
        `),
      });
      cache.modify({
        fields: {
          likedStores(existingStores) {
            const arr = Array.isArray(existingStores)
              ? [...existingStores]
              : [];
            return [...arr, newStoreRef];
          },
        },
      });
    },
  });

  const [cancelLike] = useMutation(CANCEL_LIKE, {
    update: (cache, { data: mutationData }) => {
      const cancelLikeResult = mutationData?.cancelLike;
      if (!cancelLikeResult) return;
      cache.modify({
        id: `Store:${cancelLikeResult.storeId}`,
        fields: {
          isLiked() {
            return false;
          },
        },
      });
      setAllStores((prev) =>
        prev.filter((store) => store.id !== cancelLikeResult.storeId)
      );
    },
  });

  function onClickStore(store: LikedStoreItem, type: string) {
    track("매장 클릭", {
      매장명: store.title,
      유형: type,
    });
    navigate(`/store/${store.id}`);
  }

  async function onClickLike(
    storeId: number,
    isLiked: boolean | null,
    storeTitle?: string
  ) {
    track(isLiked ? "좋아요취소 클릭" : "좋아요 클릭", {
      매장명: storeTitle,
    });
    if (isLiked) {
      await cancelLike({ variables: { storeId } });
    } else {
      await likeStore({ variables: { storeId } });
    }
  }

  return {
    allStores,
    loading,
    initLoading,
    rootRef,
    loadMoreRef,
    hasMore,
    isLoadingMore,
    onClickStore,
    onClickLike,
  };
}
